import {
  Criteria,
  Device,
  DeviceGroupData,
  DeviceGroupsCriteria,
  MyDevicesData,
  PaginatedAPIResponse,
  SortDirection,
} from '@zspace/types';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Element } from 'react-bulma-components';
import { fetchDeviceGroupDevices as fetchDeviceGroupDevicesRequest } from '../../device-groups/device-groups-service';
import { FilterInputData } from '../../shared/filter-bar/filter-bar';
import useHttpRequest from '../../shared/hooks/http-request';
import DefaultInboxTable from '../../ui/inbox-table/default-inbox-table/default-inbox-table';
import InboxTable from '../../ui/inbox-table/inbox-table';
import PageSpinner from '../../ui/page-spinner/page-spinner';
import SelectionIndicator from '../../ui/selection-indicator/selection-indicator';
import { OnTableChangeData } from '../../ui/table/types';
import { fetchWorkOrderDeviceGroupViewDevices } from '../../work-orders/work-orders-service';
import DeviceGroupInbox from './device-group-inbox/device-group-inbox';
import DeviceGroupTable from './device-group-table/device-group-table';

export type MyDevices = MyDevicesData & {
  displayUnregistered: boolean;
  displayUngrouped: boolean;
};

export type DeviceGroupInboxTableProps = {
  myDevices?: MyDevices;
  deviceGroups: DeviceGroupData[];
  fetchMoreDeviceGroups: () => void;
  hasMoreDeviceGroups: boolean;
  onManageDeviceClick?: (
    deviceGroup: string,
    devices: Device[],
    add: boolean
  ) => void;
  onDeviceGroupClick?: (deviceGroup: DeviceGroupData) => void;
  onUpdateDeviceGroupVisibilityClick?: (
    deviceGroupId: string,
    visible: boolean
  ) => void;
  onEditDeviceGroupClick?: (deviceGroup: DeviceGroupData) => void;
  onExportDeviceGroupDevicesClick?: (deviceGroupId: string) => void;
  selectedDevicesByDeviceGroup?: Map<string, Device[]>;
  leftContent?: ReactNode;
  loadingInitialData?: boolean;
  appliedFilters: [string, FilterInputData<DeviceGroupsCriteria>][];
  deviceGroupsCriteriaFilter: DeviceGroupsCriteria;
  isInvalidSelection?: (devices: Device[]) => boolean;
  workOrderId?: string;
  disableSelection?: boolean;
  disableDeviceGroupActions?: boolean;
};

const initialCriteria: Criteria = {
  itemsPerPage: 10,
  pageNumber: 1,
  search: '',
  sortBy: '',
  sortDirection: SortDirection.ASC,
};

export function DeviceGroupInboxTable({
  myDevices,
  deviceGroups,
  fetchMoreDeviceGroups,
  hasMoreDeviceGroups,
  onManageDeviceClick,
  onDeviceGroupClick,
  onUpdateDeviceGroupVisibilityClick,
  onEditDeviceGroupClick,
  onExportDeviceGroupDevicesClick,
  selectedDevicesByDeviceGroup,
  leftContent,
  appliedFilters,
  loadingInitialData,
  deviceGroupsCriteriaFilter,
  isInvalidSelection,
  workOrderId,
  disableSelection,
  disableDeviceGroupActions,
}: DeviceGroupInboxTableProps) {
  const latestDataFetchRef = useRef(0);
  const [selectedDeviceGroup, setSelectedDeviceGroup] = useState<string>();
  const [deviceGroupDevicesResponse, setDeviceGroupDevicesResponse] =
    useState<PaginatedAPIResponse<Device>>();
  const [devices, setDevices] = useState<Device[]>([]);
  const { executeHttpRequest } = useHttpRequest();
  const [loadingInitialDevices, setLoadingInitialDevices] = useState(false);
  const [criteriaFilter, setCriteriaFilter] =
    useState<Criteria>(initialCriteria);

  const tableConfig = useMemo(() => {
    return {
      ...criteriaFilter,
      pages: deviceGroupDevicesResponse?.pages ?? 1,
    };
  }, [criteriaFilter, deviceGroupDevicesResponse?.pages]);

  const selectedDevices = useMemo(() => {
    if (!selectedDevicesByDeviceGroup) return [];
    return Array.from(selectedDevicesByDeviceGroup.values()).flat();
  }, [selectedDevicesByDeviceGroup]);

  const activeDeviceGroupSelectedDevicesCount = useMemo(
    () =>
      selectedDevicesByDeviceGroup?.get(selectedDeviceGroup ?? '')?.length ?? 0,
    [selectedDeviceGroup, selectedDevicesByDeviceGroup]
  );

  const activeDeviceGroupTotalDevicesCount = useMemo(
    () => deviceGroupDevicesResponse?.total ?? 0,
    [deviceGroupDevicesResponse?.total]
  );

  const fetchDeviceGroupDevices = useCallback(
    async (
      deviceGroupId: string,
      criteria: DeviceGroupsCriteria,
      clearData?: boolean
    ) => {
      const currentRef = ++latestDataFetchRef.current;
      return executeHttpRequest({
        asyncFunction: async () => {
          let response: PaginatedAPIResponse<Device>;

          if (workOrderId) {
            response = await fetchWorkOrderDeviceGroupViewDevices(
              workOrderId,
              deviceGroupId,
              criteria
            );
          } else {
            response = await fetchDeviceGroupDevicesRequest(
              deviceGroupId,
              criteria
            );
          }

          if (currentRef !== latestDataFetchRef.current) return;

          if (clearData) {
            setDevices(response.data);
          } else {
            setDevices((prevDevices) => [...prevDevices, ...response.data]);
          }
          setDeviceGroupDevicesResponse(response);
        },
      });
    },
    [executeHttpRequest, workOrderId]
  );

  const onDeviceGroupSelect = useCallback(
    async (deviceGroupId: string) => {
      const { itemsPerPage, pageNumber, sortBy, sortDirection } =
        initialCriteria;
      const updatedCriteria = {
        ...deviceGroupsCriteriaFilter,
        itemsPerPage,
        pageNumber,
        sortBy,
        sortDirection,
      };
      setCriteriaFilter((prevCriteria) => ({
        ...initialCriteria,
        search: prevCriteria.search,
      }));

      setSelectedDeviceGroup(deviceGroupId);
      setLoadingInitialDevices(true);
      await fetchDeviceGroupDevices(deviceGroupId, updatedCriteria, true);
      setLoadingInitialDevices(false);
    },
    [deviceGroupsCriteriaFilter, fetchDeviceGroupDevices]
  );

  const onTableChange = useCallback(
    async (value: OnTableChangeData<Device>) => {
      const updatedCriteria = {
        ...value.config,
        sortBy: value.column?.key || deviceGroupsCriteriaFilter.sortBy,
      };
      setCriteriaFilter((prevCriteria) => ({
        ...prevCriteria,
        ...updatedCriteria,
      }));
      setLoadingInitialDevices(true);
      await fetchDeviceGroupDevices(
        selectedDeviceGroup!,
        {
          ...deviceGroupsCriteriaFilter,
          ...updatedCriteria,
        },
        true
      );
      setLoadingInitialDevices(false);
    },
    [deviceGroupsCriteriaFilter, fetchDeviceGroupDevices, selectedDeviceGroup]
  );

  const onDeviceClick = useCallback(
    (devices: Device[], add: boolean) => {
      onManageDeviceClick?.(selectedDeviceGroup!, devices, add);
    },
    [onManageDeviceClick, selectedDeviceGroup]
  );

  useEffect(() => {
    const refetchDevices = async () => {
      if (selectedDeviceGroup) {
        setLoadingInitialDevices(true);
        await fetchDeviceGroupDevices(
          selectedDeviceGroup,
          {
            ...deviceGroupsCriteriaFilter,
            ...criteriaFilter,
            pageNumber: initialCriteria.pageNumber,
          },
          true
        );
        setLoadingInitialDevices(false);
      }
    };

    refetchDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  const emptyContent = useMemo(() => {
    const emptyContentText =
      appliedFilters.length > 0 || tableConfig.search !== ''
        ? 'There are no devices that match the selected criteria'
        : 'There are no devices at the moment';
    return (
      <Element
        display="flex"
        justifyContent="center"
        alignItems="center"
        className="h-full"
      >
        <span className="has-text-weight-light">{emptyContentText}</span>
      </Element>
    );
  }, [appliedFilters.length, tableConfig.search]);

  const table = useMemo(() => {
    if (loadingInitialDevices) {
      return <PageSpinner />;
    }
    if (selectedDeviceGroup) {
      return (
        <DeviceGroupTable
          dataSource={devices}
          onManageDeviceClick={onDeviceClick}
          selectedDevices={selectedDevices}
          isInvalidSelection={isInvalidSelection}
          config={tableConfig}
          onChange={onTableChange}
          disableSelection={disableSelection}
          form={{
            enableSearch: false,
            leftExtra: (
              <SelectionIndicator
                selected={activeDeviceGroupSelectedDevicesCount}
                total={activeDeviceGroupTotalDevicesCount}
              />
            ),
          }}
          ignoreSalesOrderPermissions={!!workOrderId}
          empty={emptyContent}
        />
      );
    }
    return (
      <DefaultInboxTable text="Please select a device group to continue" />
    );
  }, [
    loadingInitialDevices,
    selectedDeviceGroup,
    devices,
    onDeviceClick,
    selectedDevices,
    isInvalidSelection,
    tableConfig,
    onTableChange,
    disableSelection,
    activeDeviceGroupSelectedDevicesCount,
    activeDeviceGroupTotalDevicesCount,
    workOrderId,
    emptyContent,
  ]);

  const inbox = useMemo(
    () => (
      <DeviceGroupInbox
        fetchMore={fetchMoreDeviceGroups}
        myDevices={myDevices}
        deviceGroups={deviceGroups}
        hasMore={hasMoreDeviceGroups}
        onClick={onDeviceGroupSelect}
        onClickDeviceGroup={onDeviceGroupClick}
        onUpdateDeviceGroupVisibilityClick={onUpdateDeviceGroupVisibilityClick}
        onEditDeviceGroupClick={onEditDeviceGroupClick}
        onExportDeviceGroupDevicesClick={onExportDeviceGroupDevicesClick}
        selectedDeviceGroupId={selectedDeviceGroup}
        loadingInitialData={loadingInitialData}
        disableDeviceGroupActions={disableDeviceGroupActions}
      />
    ),
    [
      fetchMoreDeviceGroups,
      myDevices,
      deviceGroups,
      hasMoreDeviceGroups,
      onDeviceGroupSelect,
      onDeviceGroupClick,
      onUpdateDeviceGroupVisibilityClick,
      onEditDeviceGroupClick,
      onExportDeviceGroupDevicesClick,
      selectedDeviceGroup,
      loadingInitialData,
      disableDeviceGroupActions,
    ]
  );

  return (
    <InboxTable
      inbox={inbox}
      table={table}
      leftContent={leftContent}
      config={tableConfig}
      enableSearch={false}
    />
  );
}

export default DeviceGroupInboxTable;
